import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard, HomeGuard } from './core';
import { AppGuard } from './core/guard/app.guard';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { ReferralResolver } from './pages/referral-management/referral.resolver';
import { pageEvents } from './shared';
import { MainComponent } from './shared/main/main.component';
import { MainResolver } from './shared/main/main.resolver';
import { AppMaintenanceGuard } from './core/guard/app-maintenance.guard';
import { PortalUsersResolver } from './pages/portal-users/portal-users.resolver';
import { WorkflowResolver } from './pages/workflows/workflow.resolver';
import { EnterpriseReferralGuard } from './core/guard/enterprise-referral.guard';

/** Global routes setup  */
const routes: Routes = [
    {
        path: 'auth',
        canActivate: [AppMaintenanceGuard, AuthGuard],
        loadChildren: () => import('@pages/login/login.module').then(m => m.LoginModule),
        data: { ...pageEvents.default }
    },
    {
        path: 'referral-auth',
        canActivate: [AppMaintenanceGuard],
        loadChildren: () => import('@pages/referral-auth/referral-auth.module').then(m => m.ReferralAuthModule),
        data: { ...pageEvents.referralAuth }
    },
    {
        path: 'home',
        resolve: { appProfileData: MainResolver },
        component: MainComponent,
        canActivate: [AppMaintenanceGuard, HomeGuard],
        children: [
            {
                path: 'dashboard',
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/dashboard/dashboard.module').then(m => m.DashboardModule),
                data: { ...pageEvents.dashboard }
            },
            { 
                path: 'broadcast', 
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/broadcast/broadcast.module').then(m => m.BroadcastModule) 
            },
            { 
                path: 'survey', 
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/survey/survey.module').then(m => m.SurveyModule) 
            },
            { 
                path: 'workflows', 
                canActivate: [AppGuard],
                resolve: { workflowEnabled: WorkflowResolver },
                loadChildren: () => import('@pages/workflows/workflows.module').then(m => m.WorkflowsModule) 
            },
            {
                path: 'organization',
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/organization/organization.module').then(m => m.OrganizationModule),
                data: { ...pageEvents.organization }
            },
            { 
                path: 'users', 
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/users/users.module').then(m => m.UsersModule) 
            },
            { 
                path: 'portal-users', 
                resolve: { referralEnabled: PortalUsersResolver },
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/portal-users/portal-users.module').then(m => m.PortalUsersModule) 
            },
            {
                path: 'referrals',
                canActivate: [AppGuard],
                canMatch: [EnterpriseReferralGuard],
                data: { enterprisePath: '/referrals' },
                loadChildren: () => import('./pages/enterprise/enterprise.module').then(m => m.EnterpriseModule)
            },
            {
                path: 'referrals',
                canActivate: [AppGuard],
                resolve: { referralEnabled: ReferralResolver },
                loadChildren: () =>
                    import('@app/pages/referral-management/referral-management.module')
                    .then(m => m.ReferralManagementModule)
            },
            { 
                path: 'iam', 
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/iam/iam.module').then(m => m.IamModule), data: { ...pageEvents.iam } },
            {
                path: 'usage',
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/usage-billing/usage-billing.module').then(m => m.UsageBillingModule),
                data: { ...pageEvents.usage }
            },
            {
                path: 'setting',
                canActivate: [AppGuard],
                loadChildren: () => import('@pages/setting/setting.module').then(m => m.SettingModule),
                data: { ...pageEvents.settings }
            },
            {
                path: 'program-reminder',
                canActivate: [AppGuard],
                loadChildren: () => import('./pages/program-reminder/program-reminder.module').then(m => m.ProgramReminderModule)
            },
            {
                path: '',
                redirectTo: 'broadcast',
                pathMatch: 'full'
            }
        ]
    },
    { path: 'not-found', component: NotFoundComponent, data: { ...pageEvents.default } },
    { path: '', redirectTo: 'home', pathMatch: 'full' },
    { path: '**', redirectTo: 'not-found' }
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule {}
